import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from "react"
import { router } from "@inertiajs/react"

type AudioState = {
  currentSampleId: number | null
  isPlaying: boolean
  currentTime: number
  duration: number
  isLoading: boolean
}

type AudioPlayerContextValue = {
  state: AudioState
  play: (sampleId: number, streamUrl: string) => void
  pause: () => void
  stop: () => void
  seek: (time: number) => void
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null)

const initialState: AudioState = {
  currentSampleId: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  isLoading: false,
}

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [state, setState] = useState<AudioState>(initialState)

  // Stop audio on Inertia navigation
  useEffect(() => {
    const removeListener = router.on("before", () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ""
      }
      setState(initialState)
    })
    return removeListener
  }, [])

  const play = useCallback((sampleId: number, streamUrl: string) => {
    const audio = audioRef.current
    if (!audio) return

    // If same sample, just resume
    if (state.currentSampleId === sampleId && audio.src) {
      audio.play()
      return
    }

    // Different sample - load and play
    setState((prev) => ({
      ...prev,
      currentSampleId: sampleId,
      isLoading: true,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
    }))

    audio.src = streamUrl
    audio.play()
  }, [state.currentSampleId])

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.src = ""
    }
    setState(initialState)
  }, [])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (audio && isFinite(time)) {
      audio.currentTime = time
    }
  }, [])

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      setState((prev) => ({
        ...prev,
        currentTime: audio.currentTime,
      }))
    }
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      setState((prev) => ({
        ...prev,
        duration: audio.duration,
      }))
    }
  }, [])

  const handlePlaying = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: true,
      isLoading: false,
    }))
  }, [])

  const handlePause = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: false,
    }))
  }, [])

  const handleWaiting = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
    }))
  }, [])

  const handleEnded = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: false,
      currentTime: 0,
    }))
  }, [])

  const handleError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: false,
      isLoading: false,
    }))
  }, [])

  return (
    <AudioPlayerContext.Provider value={{ state, play, pause, stop, seek }}>
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlaying={handlePlaying}
        onPause={handlePause}
        onWaiting={handleWaiting}
        onEnded={handleEnded}
        onError={handleError}
        preload="none"
        className="hidden"
      />
    </AudioPlayerContext.Provider>
  )
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext)
  if (!context) {
    throw new Error("useAudioPlayer must be used within AudioPlayerProvider")
  }
  return context
}
